import { cn } from '@cibli/shared';
import { Edit } from 'lucide-react';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { EleganceParams } from './schema';

import CVContainer from '../../components/CVContainer';
import EditableSection from '../../components/form/EditableSection';
import { usePageDelimitation } from '../../hooks/usePageDelimitation';
import { usePdfPrintReady } from '../../hooks/usePdfPrintReady';
import ProfilePicture from '../classic/components/ProfilePicture';
import { getContrastTextColor } from '../helpers';
import { TemplateDisplayProps } from '../types';
import {
  ComplementaryInfo,
  ContactInfo,
  Education,
  Experience,
  HobbiesAndInterests,
  Languages,
  Skills,
  Summary,
  PersonalExperiences,
} from './components/';

/** Élégance: 2 columns, 5 sections per column (left: ContactInfo, Skills, Languages, Hobbies, ComplementaryInfo; right: Summary, Experience, PersonalExperiences, Education). */
const SECTION_COUNTS = [5, 4] as const;
const SPLITABLE_SECTIONS: [number, number][] = [
  [1, 1],
  [1, 2],
  [1, 3],
];

export default function Display({
  cv,
  params,
  onEdit,
  mode = 'preview',
}: TemplateDisplayProps<EleganceParams>) {
  const { t } = useTranslation();
  const accent = params.accentColor;
  const accentTextColor = getContrastTextColor(accent);
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRefsRef = useRef<(HTMLElement | null)[][]>([]);

  const setBlockRef = useCallback((col: number, blockIndex: number) => {
    return (el: HTMLElement | null) => {
      if (!sectionRefsRef.current[col]) sectionRefsRef.current[col] = [];
      sectionRefsRef.current[col][blockIndex] = el;
    };
  }, []);

  const contentKey = [
    cv.competences?.length ?? 0,
    cv.experiences?.length ?? 0,
    cv.education?.length ?? 0,
    cv.personalExperiences?.length ?? 0,
    cv.summary?.length ?? 0,
    cv.hasRQTH ? 1 : 0,
    cv.complementaryMentions?.length ?? 0,
  ].join('-');

  const { blockOffsets, splitLayout, pageCount, pageHeightPx, isLayoutReady } = usePageDelimitation(
    sectionRefsRef,
    containerRef,
    {
      contentKey,
      bodyBottomPaddingPx: 24,
      splitableSections: SPLITABLE_SECTIONS,
      sectionCountPerColumn: SECTION_COUNTS as unknown as number[],
    },
  );

  usePdfPrintReady(mode === 'print', containerRef, isLayoutReady);

  const containerHeight = pageCount > 0 && pageHeightPx > 0 ? pageCount * pageHeightPx : undefined;

  const renderRightColumnBlocks = () => {
    const col = 1;
    const offsets = blockOffsets[col] ?? [];
    const layout = splitLayout[col] ?? [];
    let blockIndex = 0;
    const nodes: React.ReactNode[] = [];

    nodes.push(
      <div
        key="0"
        ref={setBlockRef(col, blockIndex)}
        style={{ marginTop: offsets[blockIndex] ?? 0 }}
      >
        <Summary summary={cv.summary} accent={accent} onEdit={onEdit} />
      </div>,
    );
    blockIndex++;

    const expChunks = layout[1] ?? [
      { showTitle: true, startIndex: 0, endIndex: cv.experiences?.length ?? 0 },
    ];
    expChunks.forEach((chunk, ci) => {
      nodes.push(
        <div
          key={`1-${ci}`}
          ref={setBlockRef(col, blockIndex)}
          style={{ marginTop: offsets[blockIndex] ?? 0 }}
        >
          <Experience experiences={cv.experiences} accent={accent} onEdit={onEdit} chunk={chunk} />
        </div>,
      );
      blockIndex++;
    });

    const peChunks = layout[2] ?? [
      {
        showTitle: true,
        startIndex: 0,
        endIndex: cv.personalExperiences?.length ?? 0,
      },
    ];
    peChunks.forEach((chunk, ci) => {
      nodes.push(
        <div
          key={`2-${ci}`}
          ref={setBlockRef(col, blockIndex)}
          style={{ marginTop: offsets[blockIndex] ?? 0 }}
        >
          <PersonalExperiences
            personalExperiences={cv.personalExperiences}
            accent={accent}
            onEdit={onEdit}
            chunk={chunk}
          />
        </div>,
      );
      blockIndex++;
    });

    const eduChunks = layout[3] ?? [
      { showTitle: true, startIndex: 0, endIndex: cv.education?.length ?? 0 },
    ];
    eduChunks.forEach((chunk, ci) => {
      nodes.push(
        <div
          key={`3-${ci}`}
          ref={setBlockRef(col, blockIndex)}
          style={{ marginTop: offsets[blockIndex] ?? 0 }}
        >
          <Education education={cv.education} accent={accent} onEdit={onEdit} chunk={chunk} />
        </div>,
      );
      blockIndex++;
    });

    return nodes;
  };

  return (
    <CVContainer accentColor={params.accentColor} mode={mode}>
      <div
        ref={containerRef}
        data-page-count={pageCount}
        className="relative"
        style={
          {
            ...(containerHeight != null ? { height: containerHeight } : {}),
            '--accent-text': accentTextColor,
          } as React.CSSProperties
        }
      >
        <div
          className={cn('w-full px-4 pt-2 pb-1 text-xs')}
          style={{
            color: accentTextColor,
            background: params.headerStyle === 'stripe' ? `${accent}` : 'transparent',
            borderBottom: params.headerStyle === 'badge' ? `6px solid ${accent}` : 'none',
          }}
        >
          <div className="flex items-center gap-4">
            {params.showProfilePicture && (mode === 'preview' || cv.pictureBase64) && (
              <div className="shrink-0">
                <EditableSection
                  onEdit={() => onEdit && onEdit('personalInfos')}
                  editable={onEdit !== undefined}
                  accentColor={params.headerStyle === 'badge' ? accent : '#fff'}
                  buttonPosition="right"
                  enablePageBreak={false}
                >
                  <ProfilePicture
                    imageBase64={cv.pictureBase64 || null}
                    firstName={cv.firstName}
                    lastName={cv.lastName}
                    showPlaceholder={true}
                    size="sm"
                    onClick={onEdit ? () => onEdit('personalInfos') : undefined}
                    textColor={params.headerStyle === 'stripe' ? 'text-white' : 'text-primary'}
                  />
                </EditableSection>
              </div>
            )}
            <div className={cn('flex-1', params.headerStyle === 'badge' ? 'text-black' : '')}>
              <div className="flex items-center gap-2">
                <h1 className="text-[15px] font-black uppercase tracking-widest leading-tight">
                  {cv.firstName} {cv.lastName}
                </h1>
                {onEdit && mode === 'edit' && (
                  <button
                    onClick={() => onEdit('personalInfos')}
                    className="h-5 w-5 flex items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors cursor-pointer"
                    aria-label={t('CV.editNameAria')}
                  >
                    <Edit className="h-2.5 w-2.5" />
                  </button>
                )}
              </div>
              <p className="text-[20px] opacity-90 font-bold">{cv.jobTitle}</p>
            </div>
          </div>
        </div>

        <main className="p-1">
          <div className="grid grid-cols-3 gap-2 h-full">
            <section className="flex flex-col gap-2">
              <div ref={setBlockRef(0, 0)} style={{ marginTop: blockOffsets[0]?.[0] ?? 0 }}>
                <ContactInfo cv={cv} accent={accent} onEdit={onEdit} />
              </div>
              <div ref={setBlockRef(0, 1)} style={{ marginTop: blockOffsets[0]?.[1] ?? 0 }}>
                <Skills competences={cv.competences} accent={accent} onEdit={onEdit} />
              </div>
              <div ref={setBlockRef(0, 2)} style={{ marginTop: blockOffsets[0]?.[2] ?? 0 }}>
                <Languages languages={cv.languages} accent={accent} onEdit={onEdit} />
              </div>
              <div ref={setBlockRef(0, 3)} style={{ marginTop: blockOffsets[0]?.[3] ?? 0 }}>
                <HobbiesAndInterests
                  hobbiesAndInterests={cv.hobbiesAndInterests}
                  accent={accent}
                  onEdit={onEdit}
                />
              </div>
              <div ref={setBlockRef(0, 4)} style={{ marginTop: blockOffsets[0]?.[4] ?? 0 }}>
                <ComplementaryInfo
                  hasRQTH={cv.hasRQTH}
                  complementaryMentions={cv.complementaryMentions}
                  accent={accent}
                  onEdit={onEdit}
                />
              </div>
            </section>

            {/* Right column (col 1): Summary, Experience, PersonalExperiences, Education */}
            <section className="col-span-2 flex flex-col gap-2">
              {renderRightColumnBlocks()}
            </section>
          </div>
        </main>

        {/* Logo Cibli at bottom right of each page */}
        {Array.from({ length: pageCount }, (_, i) => (
          <div
            key={i}
            className="absolute left-0 right-0 pointer-events-none"
            style={{ top: i * pageHeightPx, height: pageHeightPx }}
          >
            <div className="absolute bottom-1 right-1">
              <img src="/logo.svg" alt="Cibli" className="w-12" />
            </div>
          </div>
        ))}
      </div>
    </CVContainer>
  );
}
